import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import {
  CableCategoryState,
  CableCategoryResponse,
  CableCategory,
} from "./types/cable-types";
import { ApiError } from "./types/common-types";
import { PaginationReturn, TextStatusSearchParams } from "./types/types";

const initialState: CableCategoryState = {
  cableCategories: [],
  cableCategory: null,
  selectedCableCategory: null,
  loading: true,
  error: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// CREATE DataCategory (supports FormData for image upload)
export const createCableCategory = createAsyncThunk<
  CableCategoryResponse,
  FormData | Omit<CableCategory, "id" | "created_at" | "updated_at">,
  { rejectValue: ApiError; state: RootState }
>("cableCategories/create", async (cableCategoryData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = cableCategoryData instanceof FormData;
    const res = await axios.post(
      `${API_URL}/cable-categories`,
      cableCategoryData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return {
      message: res.data.message,
      dataCategory: res.data.data,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create data category" },
    );
  }
});

// READ (fetch all) CableCategories (with pagination)
export const fetchCableCategories = createAsyncThunk<
  CableCategoryResponse & PaginationReturn,
  TextStatusSearchParams,
  { rejectValue: ApiError; state: RootState }
>("cableCategories/fetchAll", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const {
      url,
      perPage = 30,
      search,
      status,
      category,
      country,
      sort_by,
      sort_order,
      ...queryParams
    } = urlFilters || {};

    const headers = { Authorization: `Bearer ${token}` };

    let res;

    if (url) {
      res = await axios.get(url, { headers });
    } else {
      // Build search/filter query parameters only for the base URL request
      const params: Record<string, any> = {};

      if (search) params.search = search;
      if (status) params.status = status;
      if (queryParams.page) params.page = queryParams.page;
      if (perPage) params.per_page = perPage;
      if (category) params.category = category;
      if (country) params.country = country;
      if (sort_by) params.sort_by = sort_by;
      if (sort_order) params.sort_order = sort_order;

      res = await axios.get(`${API_URL}/cable-categories`, {
        params,
        headers,
      });
    }

    return {
      message: res.data.data.message,
      cableCategories: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch cable categories" },
    );
  }
});

// READ (fetch one) DataCategory by ID
export const fetchCableCategoryById = createAsyncThunk<
  CableCategory,
  number,
  { rejectValue: ApiError; state: RootState }
>("cableCategories/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/cable-categories/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch cable category" },
    );
  }
});

// UPDATE DataCategory (supports FormData for image upload)
export const updateCableCategory = createAsyncThunk<
  CableCategory,
  { id: number; data: Partial<CableCategory> | FormData },
  { rejectValue: ApiError; state: RootState }
>("cableCategories/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = data instanceof FormData;
    const res = await axios.post(
      `${API_URL}/cable-categories/${id}?_method=PUT`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update cable category" },
    );
  }
});

// DELETE DataCategory
export const deleteCableCategory = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("cableCategories/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/cable-categories/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete cable category" },
    );
  }
});

// Slice
const cableCategorySlice = createSlice({
  name: "cableCategories",
  initialState,
  reducers: {
    resetCableCategoryState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedCableCategory = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createCableCategory.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createCableCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.cableCategory) {
          state.cableCategories.unshift(action.payload.cableCategory);
        }
      })
      .addCase(createCableCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to create cable category";
      })

      // Fetch All
      .addCase(fetchCableCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCableCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.cableCategories = action.payload.cableCategories || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchCableCategories.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch cable categories";
      })

      // Fetch One
      .addCase(fetchCableCategoryById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedCableCategory = null;
      })
      .addCase(fetchCableCategoryById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedCableCategory = action.payload;
      })
      .addCase(fetchCableCategoryById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch cable category";
      })

      // Update
      .addCase(updateCableCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateCableCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.cableCategories = state.cableCategories.map((category) =>
          category.id === action.payload.id ? action.payload : category,
        );
        if (state.selectedCableCategory?.id === action.payload.id) {
          state.selectedCableCategory = action.payload;
        }
      })
      .addCase(updateCableCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to update cable category";
      })

      // Delete
      .addCase(deleteCableCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteCableCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.cableCategories = state.cableCategories.filter(
          (category) => category.id !== action.payload,
        );
      })
      .addCase(deleteCableCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to delete cable category";
      });
  },
});

export const { resetCableCategoryState } = cableCategorySlice.actions;
export default cableCategorySlice.reducer;
